import type { Post } from '@/types';
import type { CcPlatformSdk } from '@cc-consulting-nv/ccsdk';
import { resolveApiBaseUrl } from '@/utils/env';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

export interface AudioViewSegment {
  post_ulid: string;
  start_second: number;
  end_second: number;
  last_position: number;
}

export interface AudioViewBatch {
  views: AudioViewSegment[];
}

export interface AudioViewPayload {
  data: string;
  views: AudioViewSegment[];
  timestamp: number;
  client_id: string;
}

export interface AudioTrack {
  id: string;
  sourcePost?: {
    ulid: string;
    id?: string;
  };
}

class AudioViewTracker {
  private static instance: AudioViewTracker | null = null;

  private pendingViews: AudioViewSegment[] = [];
  private batchInterval = 60000; // Send batches every minute
  private segmentDuration = 5; // 5-second segments
  private batchTimer: ReturnType<typeof setInterval> | null = null;
  private lastTrackedTimes = new Map<string, number>(); // postUlid -> lastTrackedTime
  private isInitialized = false;
  private clientId: string;
  private sdk: CcPlatformSdk | null = null;

  private constructor() {
    // Generate a temporary client ID, will be replaced with FingerprintJS visitorId
    this.clientId = this.generateClientId();

    // Initialize FingerprintJS for more reliable visitor identification
    this.initializeFingerprint();

    if (typeof window !== 'undefined') {
      // Send any pending views before page unload
      window.addEventListener('beforeunload', () => {
        this.beforeUnload();
      });
    }
  }

  private async initializeFingerprint(): Promise<void> {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      this.clientId = result.visitorId;

      // Store for persistence
      try {
        localStorage.setItem('fp_visitor_id', result.visitorId);
      } catch {
        // localStorage may not be available
      }
    } catch (error) {
      console.warn('[AudioViewTracker] FingerprintJS failed, using fallback:', error);
      // Try to recover from localStorage
      try {
        const stored = localStorage.getItem('fp_visitor_id');
        if (stored) {
          this.clientId = stored;
        }
      } catch {
        // localStorage not available, keep the fallback clientId
      }
    }
  }

  public static getInstance(): AudioViewTracker {
    if (!AudioViewTracker.instance) {
      AudioViewTracker.instance = new AudioViewTracker();
    }
    return AudioViewTracker.instance;
  }

  public initialize(sdk: CcPlatformSdk): void {
    if (this.isInitialized) return;

    this.sdk = sdk;
    this.startBatchTimer();
    this.isInitialized = true;
  }

  /**
   * Immediately notify the backend that a song started playing.
   * Uses the /v1/player-events endpoint for webhook notifications.
   * Also sends a start_second=0 audio-view segment for listening tracking.
   * Called from useAudioPlayer.play() when a new song begins.
   */
  public notifySongStarted(postUlid: string): void {
    if (!postUlid) return;

    // Send player event for webhook notifications (works with or without auth)
    this.sendPlayerEvent('started', postUlid);

    // Also send audio-view segment for listening tracking (requires SDK)
    if (this.sdk) {
      const segment: AudioViewSegment = {
        post_ulid: postUlid,
        start_second: 0,
        end_second: this.segmentDuration - 1,
        last_position: 0,
      };

      const payload = this.createPayload([segment]);
      this.sdk.sendAudioViews(payload).catch((err) => {
        console.warn('[AudioViewTracker] notifySongStarted audio-view failed:', err);
      });
    }
  }

  /**
   * Notify the backend that the user skipped a song.
   * Called from useAudioPlayer.skipNext().
   */
  public notifySongSkipped(postUlid: string): void {
    this.sendPlayerEvent('skipped', postUlid);
  }

  /**
   * Notify the backend that the user restarted a song (hit rewind/previous on current track).
   * Called from useAudioPlayer.playPrevious().
   */
  public notifySongRestarted(postUlid: string): void {
    this.sendPlayerEvent('restarted', postUlid);
  }

  /**
   * Send a lightweight player event to the backend via POST /v1/player-events.
   * Works with or without auth - anonymous users send client_id (FingerprintJS) for identification.
   */
  private sendPlayerEvent(action: 'started' | 'skipped' | 'restarted', postUlid: string): void {
    if (!postUlid) return;

    const apiBaseUrl = resolveApiBaseUrl();
    const token = localStorage.getItem('auth_token');

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    fetch(`${apiBaseUrl}/v1/songs/player-events`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action,
        post_ulid: postUlid,
        client_id: this.clientId,
      }),
    }).catch((err) => {
      console.warn(`[AudioViewTracker] sendPlayerEvent(${action}) failed:`, err);
    });
  }

  public trackSegment(postUlid: string, startSecond: number, endSecond: number, currentPosition: number): void {
    // Normalize to 5-second segments
    const segmentStart = Math.floor(startSecond / this.segmentDuration) * this.segmentDuration;
    const segmentEnd = Math.min(segmentStart + this.segmentDuration - 1, endSecond);

    // Avoid duplicate segments
    const isDuplicate = this.pendingViews.some(
      (view) => view.post_ulid === postUlid && view.start_second === segmentStart && view.end_second === segmentEnd
    );

    if (isDuplicate) {
      return;
    }

    this.pendingViews.push({
      post_ulid: postUlid,
      start_second: segmentStart,
      end_second: segmentEnd,
      last_position: Math.floor(currentPosition),
    });

    // If we have too many pending views, send immediately
    if (this.pendingViews.length >= 20) {
      void this.sendBatch();
    }
  }

  public trackTimeUpdate(track: AudioTrack | Post, currentTime: number, duration: number): void {
    // Handle both AudioTrack and Post types
    let postUlid: string | undefined;

    // Check if it's an AudioTrack with sourcePost
    const audioTrack = track as AudioTrack;
    if (audioTrack.sourcePost?.ulid) {
      postUlid = audioTrack.sourcePost.ulid;
    } else {
      // Otherwise treat as Post
      const post = track as Post;
      postUlid = post.ulid;
    }

    if (!postUlid) {
      return; // Can't track without post ULID
    }

    const lastTrackedTime = this.lastTrackedTimes.get(postUlid) || 0;
    const currentSecond = Math.floor(currentTime);
    const lastSecond = Math.floor(lastTrackedTime);

    // Track if we've moved to a new 5-second segment
    const currentSegment = Math.floor(currentSecond / this.segmentDuration);
    const lastSegment = Math.floor(lastSecond / this.segmentDuration);

    if (currentSegment !== lastSegment && currentSecond > lastSecond) {
      const segmentStart = Math.floor(lastSecond / this.segmentDuration) * this.segmentDuration;
      const segmentEnd = Math.min(segmentStart + this.segmentDuration - 1, Math.floor(duration));

      if (segmentEnd >= segmentStart) {
        this.trackSegment(postUlid, segmentStart, segmentEnd, currentSecond);
      }
    }

    this.lastTrackedTimes.set(postUlid, currentTime);
  }

  public onSeek(track: AudioTrack | Post, newTime: number): void {
    // Handle both AudioTrack and Post types
    let postUlid: string | undefined;

    // Check if it's an AudioTrack with sourcePost
    const audioTrack = track as AudioTrack;
    if (audioTrack.sourcePost?.ulid) {
      postUlid = audioTrack.sourcePost.ulid;
    } else {
      // Otherwise treat as Post
      const post = track as Post;
      postUlid = post.ulid;
    }

    if (!postUlid) {
      return;
    }

    // Update the last tracked time to prevent gaps in tracking
    this.lastTrackedTimes.set(postUlid, newTime);
  }

  public async sendBatch(): Promise<void> {
    if (this.pendingViews.length === 0 || !this.sdk) return;

    const views = [...this.pendingViews];
    this.pendingViews = [];

    try {
      const payload = this.createPayload(views);

      // Use SDK to send audio views
      await this.sdk.sendAudioViews(payload);
    } catch (error) {
      console.error('Failed to send audio view batch:', error);

      // Re-add views to pending if they failed (with limit to prevent infinite growth)
      if (this.pendingViews.length < 100) {
        this.pendingViews.unshift(...views.slice(0, 50)); // Only keep first 50 to prevent overflow
      }
    }
  }

  private startBatchTimer(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }

    this.batchTimer = setInterval(() => {
      void this.sendBatch();
    }, this.batchInterval);
  }

  private beforeUnload(): void {
    if (this.pendingViews.length > 0 && this.sdk) {
      try {
        // Create payload
        const payload = {
          views: [...this.pendingViews],
          timestamp: Date.now(),
          client_id: this.clientId,
        };

        // Try SDK method first (async - may not complete before unload, but we try)
        // This uses the SDK's HTTP client with proper auth and error handling
        this.sdk.sendAudioViews(payload).catch(() => {
          // Silent fail - sendBeacon backup below will handle delivery
        });

        // Also use sendBeacon for guaranteed synchronous delivery during page unload
        // sendBeacon is a browser API that requires a full URL and can't use SDK's HTTP client
        // We use resolveApiBaseUrl() which should match the SDK's base URL
        // This ensures delivery even if the async SDK call doesn't complete
        const apiBaseUrl = resolveApiBaseUrl();
        const fullUrl = `${apiBaseUrl}/v1/audio-views`;
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(fullUrl, blob);
      } catch (error) {
        console.error('Error sending audio views on page unload:', error);
      }
    }
  }

  private generateClientId(): string {
    // Generate a unique client ID based on browser fingerprint and timestamp
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2);
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 50) : 'unknown';
    const screen =
      typeof window !== 'undefined' && window.screen ? `${window.screen.width}x${window.screen.height}` : 'unknown';

    const fingerprint = btoa(`${userAgent}-${screen}-${timestamp}-${random}`).slice(0, 32);
    return fingerprint;
  }

  private createPayload(views: AudioViewSegment[]): AudioViewPayload {
    return {
      data: '',  // Empty string placeholder for data field
      views,
      timestamp: Date.now(),
      client_id: this.clientId,
    };
  }

  public destroy(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }

    // Send any remaining views
    if (this.pendingViews.length > 0) {
      void this.sendBatch();
    }

    this.pendingViews = [];
    this.lastTrackedTimes.clear();
    this.isInitialized = false;
    AudioViewTracker.instance = null;
  }

  // Getter methods for debugging/monitoring
  public getPendingViewsCount(): number {
    return this.pendingViews.length;
  }

  public getTrackedPosts(): string[] {
    return Array.from(this.lastTrackedTimes.keys());
  }
}

// Export singleton instance
export const audioViewTracker = AudioViewTracker.getInstance();
export default audioViewTracker;

// Expose to window for debugging
if (typeof window !== 'undefined') {
  Object.defineProperty(window, 'audioViewTracker', {
    get: () => audioViewTracker,
    configurable: true,
  });
}
